/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetAuthClientAudienceRequest,
  GetAuthClientAudienceSecurity,
} from "../models/operations/getauthclientaudience.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AuthClientAudienceQueryData,
  buildAuthClientAudienceQuery,
  prefetchAuthClientAudience,
  queryKeyAuthClientAudience,
} from "./authClientAudience.core.js";
export {
  type AuthClientAudienceQueryData,
  buildAuthClientAudienceQuery,
  prefetchAuthClientAudience,
  queryKeyAuthClientAudience,
};

export type AuthClientAudienceQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getAuthClientAudience authClients
 *
 * @remarks
 * List who may use an auth client: the rules naming it and the broader rules that reach it. Requires org:read.
 */
export function useAuthClientAudience(
  request: GetAuthClientAudienceRequest,
  security?: GetAuthClientAudienceSecurity | undefined,
  options?: QueryHookOptions<
    AuthClientAudienceQueryData,
    AuthClientAudienceQueryError
  >,
): UseQueryResult<AuthClientAudienceQueryData, AuthClientAudienceQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAuthClientAudienceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getAuthClientAudience authClients
 *
 * @remarks
 * List who may use an auth client: the rules naming it and the broader rules that reach it. Requires org:read.
 */
export function useAuthClientAudienceSuspense(
  request: GetAuthClientAudienceRequest,
  security?: GetAuthClientAudienceSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AuthClientAudienceQueryData,
    AuthClientAudienceQueryError
  >,
): UseSuspenseQueryResult<
  AuthClientAudienceQueryData,
  AuthClientAudienceQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAuthClientAudienceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAuthClientAudienceData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: AuthClientAudienceQueryData,
): AuthClientAudienceQueryData | undefined {
  const key = queryKeyAuthClientAudience(...queryKeyBase);

  return client.setQueryData<AuthClientAudienceQueryData>(key, data);
}

export function invalidateAuthClientAudience(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "getAudience", ...queryKeyBase],
  });
}

export function invalidateAllAuthClientAudience(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "getAudience"],
  });
}
